import type { SxProps } from '@mui/material';
import { Box, InputAdornment } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { FieldProps } from 'formik';
import { Field } from 'formik';
import React from 'react';

import TextInputBox from '@components/TextInputBox';
import Textarea from '@components/Textarea/Textarea';

import { body, heading } from '@constants/fonts';

type TextType = 'email' | 'number' | 'tel' | 'text-area' | 'text' | 'password';

interface FormikTextFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  type?: TextType;
  placeholder?: string;
  isHeaderItem?: boolean;
  isHeaderTitle?: boolean;
  autoComplete?: string;
  onChange?: (event: number | string) => void;
  charLimit?: number;
  /** Text areas only: allow the user to drag the field taller (default true) */
  expandable?: boolean;
  /** Text areas only: fixed height, so the field does not grow with its text */
  height?: number | string;
  fullWidth?: boolean;
  endAdornment?: React.ReactNode;
  helperText?: string;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

const FormikTextField: React.FC<FormikTextFieldProps> = ({
  charLimit,
  expandable,
  height,
  isHeaderItem = false,
  isHeaderTitle = false,
  type = 'text',
  fullWidth = false,
  endAdornment,
  helperText,
  disabled = false,
  onKeyDown,
  ...props
}) => {
  const { name, label, required, placeholder, onChange } = props;
  const { palette } = useTheme();

  const headerItemStyling: SxProps = {
    fontFamily: `${body}`,
    fontSize: '16px',
    paddingTop: '5px',
    color: palette.text.primary,
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: palette.background.default,
  };

  const headerTitleStyling: SxProps = {
    fontFamily: `${heading}`,
    fontSize: '44px',
    lineHeight: '58px',
    color: palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: palette.background.default,
  };

  return (
    <Field name={name}>
      {({ field, meta }: FieldProps<string>) => {
        return type !== 'text-area' ? (
          <TextInputBox
            InputProps={{
              endAdornment: endAdornment ? (
                <InputAdornment position="end" disableTypography>
                  <Box sx={{ fontSize: 13 }}>{endAdornment}</Box>
                </InputAdornment>
              ) : null,
            }}
            width={fullWidth ? '100%' : undefined}
            hasError={Boolean(meta.error && meta.touched)}
            label={label}
            helperText={meta.touched && meta.error ? meta.error : helperText}
            placeholder={placeholder}
            required={required}
            type={type}
            disabled={disabled}
            onKeyDown={onKeyDown}
            {...field}
            {...props}
            onChange={(e) => {
              field.onChange(e);
              onChange?.(e.target.value);
            }}
            sx={
              // eslint-disable-next-line no-nested-ternary
              isHeaderItem
                ? { ...headerItemStyling }
                : isHeaderTitle
                  ? { ...headerTitleStyling }
                  : undefined
            }
          />
        ) : (
          <Textarea
            width={fullWidth ? '100%' : undefined}
            charLimit={charLimit}
            disabled={disabled}
            expandable={expandable}
            height={height}
            label={label}
            message={meta.touched ? meta.error : ''}
            placeholder={placeholder}
            required={required}
            {...field}
          />
        );
      }}
    </Field>
  );
};

export default FormikTextField;
